import { Button } from "@/components/design-system/button";
import { cn } from "@/utils/utils";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import { Equal, Trash2, ChevronDown, X } from "lucide-react";
import { UnitAddCircleIcon } from "@/utils/svg";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Controller,
  type Control,
  useFieldArray,
  useForm,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { z } from "zod";
import { uploadCourseUnitService } from "@/service/course.service";
import {
  isBuilderCoursePublished,
  toCreateCoursePayload,
  toUpdateCoursePayload,
} from "@/service/course.mappers";
import type {
  CourseBuilderFormValues,
  CourseBuilderModel,
  CreateCoursePayload,
  UpdateCoursePayload,
} from "@/types/course.model";
import { successToast } from "@/utils/constants";

/** Allows production grade/lesson/unit names (e.g. `G#-t1`, `1.1 Welcome`, `L1 : Study`) */
const nameSchema = z
  .string()
  .min(1, "Required")
  .max(250, "Name is too long")
  .refine((v) => v.trim().length > 0, "Required");

function buildFormSchema(mode: "create" | "edit") {
  const unitBase = z.object({
    id: z.string(),
    name: nameSchema,
    s3_upload_id: z.string().optional(),
    s3_path: z.string().optional(),
    uploaded_file_name: z.string().optional(),
    upload_course_name: z.string().optional(),
    upload_lesson_name: z.string().optional(),
    upload_unit_name: z.string().optional(),
    file_name: z.string().optional(),
  });

  const unitSchema =
    mode === "create"
      ? unitBase.superRefine((unit, ctx) => {
          if (!unit.s3_upload_id?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Upload unit zip file",
              path: ["s3_upload_id"],
            });
          }
        })
      : unitBase.superRefine((unit, ctx) => {
          const hasUpload = Boolean(unit.s3_upload_id?.trim());
          const hasLegacyFile = Boolean(unit.file_name?.trim());
          if (!hasUpload && !hasLegacyFile) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Upload unit zip file",
              path: ["s3_upload_id"],
            });
          }
        });

  return z.object({
    grade_name: nameSchema,
    terms: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        isCollapsed: z.boolean().optional(),
        lessons: z
          .array(
            z.object({
              id: z.string(),
              name: nameSchema,
              isCollapsed: z.boolean().optional(),
              units: z.array(unitSchema).min(1),
            })
          )
          .min(1),
      })
    ).min(1),
  });
}

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

function courseBuilderToFormValues(initialValue: CourseBuilderModel): FormValues {
  return {
    grade_name: initialValue.grade_name,
    terms: initialValue.terms.map((t) => ({
      ...t,
      lessons: t.lessons.map((l) => ({
        ...l,
        units: l.units.map((u) => {
          const hasUpload = Boolean(u.s3_upload_id?.trim());
          return {
            ...u,
            s3_upload_id: u.s3_upload_id ?? "",
            s3_path: u.s3_path,
            uploaded_file_name:
              u.uploaded_file_name ?? u.s3_path ?? u.file_name,
            file_name: u.file_name,
            upload_course_name: hasUpload ? initialValue.grade_name : "",
            upload_lesson_name: hasUpload ? l.name : "",
            upload_unit_name: hasUpload ? u.name : "",
          };
        }),
      })),
    })),
  };
}

const uid = () => Math.random().toString(36).slice(2, 10);

const emptyUnit = () => ({
  id: `u-${uid()}`,
  name: "",
  s3_upload_id: "",
  upload_course_name: "",
  upload_lesson_name: "",
  upload_unit_name: "",
});

/**
 * Create only: clears upload when grade/lesson/unit names change after upload
 * (upload-unit API bakes names into S3 path). Edit keeps s3_upload_id when renaming.
 */
function UnitUploadPathWatcher({
  mode,
  watch,
  setValue,
  termIndex,
  lessonIndex,
  unitIndex,
}: {
  mode: "create" | "edit";
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  termIndex: number;
  lessonIndex: number;
  unitIndex: number;
}) {
  const base = `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}` as const;

  const gradeName = watch("grade_name") as string;
  const lessonName = watch(`terms.${termIndex}.lessons.${lessonIndex}.name`) as string;
  const unitName = watch(`${base}.name`) as string;
  const s3UploadId = watch(`${base}.s3_upload_id`) as string;
  const snapCourse = watch(`${base}.upload_course_name`) as string;
  const snapLesson = watch(`${base}.upload_lesson_name`) as string;
  const snapUnit = watch(`${base}.upload_unit_name`) as string;

  useEffect(() => {
    if (mode === "edit") return;
    if (!s3UploadId?.trim() || !snapCourse?.trim()) return;

    const grade = (gradeName || "").trim();
    const lesson = (lessonName || "").trim();
    const unit = (unitName || "").trim();

    if (
      grade !== snapCourse.trim() ||
      lesson !== snapLesson.trim() ||
      unit !== snapUnit.trim()
    ) {
      setValue(`${base}.s3_upload_id`, "");
      setValue(`${base}.s3_path`, "");
      setValue(`${base}.uploaded_file_name`, "");
      setValue(`${base}.upload_course_name`, "");
      setValue(`${base}.upload_lesson_name`, "");
      setValue(`${base}.upload_unit_name`, "");
    }
  }, [
    mode,
    gradeName,
    lessonName,
    unitName,
    s3UploadId,
    snapCourse,
    snapLesson,
    snapUnit,
    base,
    setValue,
  ]);

  return null;
}

function LessonSection({
  control,
  setValue,
  watch,
  termIndex,
  lessonIndex,
  lessonId,
  sensors,
  lessonLabel,
  lessonError,
  onRemoveLesson,
  mode,
}: {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  termIndex: number;
  lessonIndex: number;
  lessonId: string;
  sensors: any;
  lessonLabel: string;
  lessonError: any;
  onRemoveLesson: () => void;
  mode: "create" | "edit";
}) {
  const unitsPath = `terms.${termIndex}.lessons.${lessonIndex}.units` as const;
  const unitsArray = useFieldArray({ control, name: unitsPath });

  const lessonCollapsed = Boolean(
    watch(`terms.${termIndex}.lessons.${lessonIndex}.isCollapsed`) as any
  );

  const gradeName = watch("grade_name") as string;

  const addUnit = () => {
    unitsArray.append(emptyUnit() as any);
  };

  return (
    <SortableRow
      id={lessonId}
      className="rounded-[12px] overflow-hidden border border-[#EDEDED]"
      handleClassName="bottom-auto h-[40px]"
      contentClassName="pl-0"
    >
      <div className="bg-[#F6F7FA] h-[52px] flex items-center justify-between pl-[56px] pr-4">
        <div className="text-[14px] font-semibold text-[#1F1F1F]">
          {lessonLabel}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-2 rounded-full hover:bg-gray-200 text-[#FF152C]"
            onClick={onRemoveLesson}
            aria-label="Delete lesson"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="p-2 rounded-full hover:bg-gray-200"
            onClick={() => {
              setValue(
                `terms.${termIndex}.lessons.${lessonIndex}.isCollapsed`,
                !lessonCollapsed
              );
            }}
          >
            <ChevronDown
              className={cn(
                "w-5 h-5 transition-transform",
                lessonCollapsed ? "rotate-0" : "rotate-180"
              )}
            />
          </button>
        </div>
      </div>

      {!lessonCollapsed && (
        <div className="p-4">
          <div className="text-[14px] font-medium text-[#1F1F1F] mb-2">
            Lesson Name
          </div>
          <Controller
            control={control}
            name={`terms.${termIndex}.lessons.${lessonIndex}.name`}
            render={({ field }) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter Name"
                error={lessonError?.name?.message}
                className="max-w-[520px]"
              />
            )}
          />

          <div className="mt-6 space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                const oldIndex = unitsArray.fields.findIndex(
                  (u) => u.id === active.id
                );
                const newIndex = unitsArray.fields.findIndex(
                  (u) => u.id === over.id
                );
                unitsArray.move(oldIndex, newIndex);
              }}
            >
              <SortableContext
                items={unitsArray.fields.map((u) => u.id)}
                strategy={verticalListSortingStrategy}
              >
                {unitsArray.fields.map((unitField, unitIndex) => {
                  const unitErr = (lessonError?.units?.[unitIndex] as any) || {};
                  return (
                    <SortableRow
                      key={unitField.id}
                      id={unitField.id}
                      className="rounded-[12px] overflow-hidden border border-[#EDEDED]"
                    >
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-start min-w-0">
                        <div>
                          <div className="text-[14px] font-medium text-[#1F1F1F] mb-2">
                            {`Unit ${unitIndex + 1} Name`}
                          </div>
                          <Controller
                            control={control}
                            name={`terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.name`}
                            render={({ field }) => (
                              <TextField
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Enter Name"
                                error={unitErr?.name?.message}
                              />
                            )}
                          />
                        </div>

                        <div>
                          <UnitUploadPathWatcher
                            mode={mode}
                            watch={watch}
                            setValue={setValue}
                            termIndex={termIndex}
                            lessonIndex={lessonIndex}
                            unitIndex={unitIndex}
                          />
                          <Controller
                            control={control}
                            name={`terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.s3_upload_id`}
                            render={({ field: s3Field }) => (
                              <UnitFileField
                                gradeName={gradeName}
                                lessonName={
                                  watch(
                                    `terms.${termIndex}.lessons.${lessonIndex}.name`
                                  ) as string
                                }
                                unitName={
                                  watch(
                                    `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.name`
                                  ) as string
                                }
                                s3UploadId={s3Field.value}
                                s3Path={
                                  watch(
                                    `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.s3_path`
                                  ) as string | undefined
                                }
                                uploadedFileName={
                                  watch(
                                    `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.uploaded_file_name`
                                  ) as string | undefined
                                }
                                onUploaded={(data) => {
                                  const unitBase = `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}`;
                                  const course = gradeName.trim();
                                  const lesson = (
                                    watch(
                                      `terms.${termIndex}.lessons.${lessonIndex}.name`
                                    ) as string
                                  ).trim();
                                  const unit = (
                                    watch(
                                      `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}.name`
                                    ) as string
                                  ).trim();
                                  s3Field.onChange(data.s3_upload_id);
                                  setValue(`${unitBase}.s3_path`, data.s3_path);
                                  setValue(
                                    `${unitBase}.uploaded_file_name`,
                                    data.s3_path
                                  );
                                  setValue(`${unitBase}.upload_course_name`, course);
                                  setValue(`${unitBase}.upload_lesson_name`, lesson);
                                  setValue(`${unitBase}.upload_unit_name`, unit);
                                }}
                                onClear={() => {
                                  const unitBase = `terms.${termIndex}.lessons.${lessonIndex}.units.${unitIndex}`;
                                  s3Field.onChange("");
                                  setValue(`${unitBase}.s3_path`, "");
                                  setValue(`${unitBase}.uploaded_file_name`, "");
                                  setValue(`${unitBase}.upload_course_name`, "");
                                  setValue(`${unitBase}.upload_lesson_name`, "");
                                  setValue(`${unitBase}.upload_unit_name`, "");
                                }}
                                error={unitErr?.s3_upload_id?.message}
                                actions={
                                  <UnitRowActions
                                    showDelete={unitIndex > 0}
                                    onDelete={() => unitsArray.remove(unitIndex)}
                                    onAdd={addUnit}
                                  />
                                }
                              />
                            )}
                          />
                        </div>
                      </div>
                    </SortableRow>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}
    </SortableRow>
  );
}

function TermSection({
  control,
  setValue,
  watch,
  termIndex,
  termId,
  termNameFallback,
  sensors,
  termErrors,
  onRemoveTerm,
  mode,
}: {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  termIndex: number;
  termId: string;
  termNameFallback: string;
  sensors: any;
  termErrors: any;
  onRemoveTerm: () => void;
  mode: "create" | "edit";
}) {
  const lessonsPath = `terms.${termIndex}.lessons` as const;
  const lessonsArray = useFieldArray({ control, name: lessonsPath });

  const addLesson = () => {
    const idx = lessonsArray.fields.length + 1;
    lessonsArray.append({
      id: `l-${uid()}`,
      name: `Lesson ${idx}`,
      units: [emptyUnit()],
    } as any);
  };

  return (
    <div key={termId} className="border border-[#EDEDED] rounded-[12px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="text-[16px] font-semibold text-[#1F1F1F]">
          {(watch(`terms.${termIndex}.name`) as string) || termNameFallback}
        </div>
        <div className="flex items-center gap-3">
          {termIndex > 0 && (
            <button
              type="button"
              className="text-[#FF152C] text-[14px] font-medium flex items-center gap-2"
              onClick={onRemoveTerm}
            >
              Delete <Trash2 className="w-4 h-4" />
            </button>
          )}
          <Button
            type="button"
            variant="primary-outline"
            size="md"
            className="h-[36px] px-4 text-[#5C88C5]"
            onClick={addLesson}
          >
            Add Lesson +
          </Button>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => {
            const { active, over } = e;
            if (!over || active.id === over.id) return;
            const oldIndex = lessonsArray.fields.findIndex(
              (l) => l.id === active.id
            );
            const newIndex = lessonsArray.fields.findIndex(
              (l) => l.id === over.id
            );
            lessonsArray.move(oldIndex, newIndex);
          }}
        >
          <SortableContext
            items={lessonsArray.fields.map((l) => l.id)}
            strategy={verticalListSortingStrategy}
          >
            {lessonsArray.fields.map((lessonField, lessonIndex) => (
              <LessonSection
                key={lessonField.id}
                control={control}
                setValue={setValue}
                watch={watch}
                termIndex={termIndex}
                lessonIndex={lessonIndex}
                lessonId={lessonField.id}
                sensors={sensors}
                lessonLabel={`Lesson ${lessonIndex + 1}`}
                lessonError={(termErrors.lessons?.[lessonIndex] as any) || {}}
                onRemoveLesson={() => lessonsArray.remove(lessonIndex)}
                mode={mode}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function SortableRow({
  id,
  children,
  className,
  disabled,
  handleClassName,
  contentClassName,
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  handleClassName?: string;
  contentClassName?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative",
        isDragging && "opacity-70",
        className
      )}
    >
      <div
        className={cn(
          "absolute left-1.5 top-1.5 bottom-1.5 w-[40px] flex items-center justify-center rounded-[8px] bg-[#2F2F2F] cursor-grab touch-none active:cursor-grabbing",
          handleClassName
        )}
        {...attributes}
        {...listeners}
      >
        <Equal className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className={cn("pl-[52px]", contentClassName)}>{children}</div>
    </div>
  );
}

function TextField({
  value,
  onChange,
  placeholder,
  error,
  className,
}: {
  value?: string;
  onChange: (v: string) => void;
  placeholder: string;
  error?: string;
  className?: string;
}) {
  return (
    <div className={cn("w-full", className)}>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full h-[48px] rounded-[12px] border px-4 text-[14px] font-medium outline-none",
          error ? "border-[#ef4444]" : "border-[#E5E7EB]"
        )}
      />
      {error ? (
        <div className="mt-1 text-[#ef4444] text-[12px] font-medium">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function UnitRowActions({
  showDelete,
  onDelete,
  onAdd,
}: {
  showDelete: boolean;
  onDelete: () => void;
  onAdd: () => void;
}) {
  return (
    <div className="flex w-[76px] shrink-0 items-center justify-start gap-3">
      {showDelete ? (
        <button
          type="button"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-[#FF152C] hover:opacity-80"
          onClick={onDelete}
          aria-label="Delete unit"
        >
          <Trash2 className="h-8 w-8" strokeWidth={1.5} />
        </button>
      ) : null}
      <button
        type="button"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center border-0 bg-transparent p-0 hover:opacity-90"
        onClick={onAdd}
        aria-label="Add unit"
      >
        <UnitAddCircleIcon className="h-8 w-8 block" />
      </button>
    </div>
  );
}

function UnitFileField({
  gradeName,
  lessonName,
  unitName,
  s3UploadId,
  s3Path,
  uploadedFileName,
  onUploaded,
  onClear,
  error,
  actions,
}: {
  gradeName: string;
  lessonName: string;
  unitName: string;
  s3UploadId?: string;
  s3Path?: string;
  uploadedFileName?: string;
  onUploaded: (data: { s3_upload_id: string; s3_path: string }) => void;
  onClear: () => void;
  error?: string;
  actions?: ReactNode;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const displayName =
    s3Path || uploadedFileName || (s3UploadId ? "Uploaded" : undefined);

  const handlePickFile = () => {
    const grade = gradeName?.trim();
    const lesson = lessonName?.trim();
    const unit = unitName?.trim();

    if (!grade || !lesson || !unit) {
      setUploadError(
        "Enter grade name, lesson name, and unit name before uploading."
      );
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".zip,application/zip";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const isZip =
        file.name.toLowerCase().endsWith(".zip") ||
        file.type === "application/zip" ||
        file.type === "application/x-zip-compressed";

      if (!isZip) {
        setUploadError("Please upload a .zip file.");
        return;
      }

      setUploadError(undefined);
      setIsUploading(true);
      try {
        const { data } = await uploadCourseUnitService(file, {
          course_name: grade,
          lesson_name: lesson,
          unit_name: unit,
        });
        onUploaded(data.data);
        successToast("Upload successful", "Unit file uploaded successfully.");
      } catch (e) {
        console.error(e);
        setUploadError("Upload failed. Please try again.");
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  return (
    <div className="w-full">
      <div className="text-[14px] font-medium text-[#1F1F1F] mb-2">
        Upload File
      </div>

      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "w-full h-[48px] rounded-[12px] border bg-white flex items-center gap-2 pl-4 pr-1.5",
              error || uploadError ? "border-[#ef4444]" : "border-[#D1D5DB]"
            )}
          >
            <span
              className={cn(
                "flex-1 min-w-0 text-[14px] font-normal truncate",
                displayName ? "text-[#1F1F1F]" : "text-[#9CA3AF]"
              )}
            >
              {displayName || "No file uploaded"}
            </span>
            {s3UploadId ? (
              <button
                type="button"
                className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center hover:bg-gray-100"
                onClick={() => {
                  setUploadError(undefined);
                  onClear();
                }}
                aria-label="Remove upload"
                disabled={isUploading}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
            <button
              type="button"
              className="shrink-0 h-[32px] w-[160px] rounded-[10px] bg-[#2D3748] text-white text-[14px] font-medium hover:bg-[#252f3f] disabled:opacity-60 flex items-center justify-center"
              disabled={isUploading}
              onClick={handlePickFile}
            >
              {isUploading ? "Uploading…" : "Upload file"}
            </button>
          </div>

          <p className="mt-2 text-[12px] text-[#7B7B7B] leading-[18px]">
          Upload unit content as a .zip file
          </p>

          {uploadError || error ? (
            <div className="mt-1 text-[#ef4444] text-[12px] font-medium">
              {uploadError || error}
            </div>
          ) : null}
        </div>

        {actions ? (
          <div className="flex h-[44px] shrink-0 items-center">{actions}</div>
        ) : null}
      </div>
    </div>
  );
}

type CourseBuilderFormProps =
  | {
      mode: "create";
      initialValue?: CourseBuilderModel;
      onSubmit: (payload: CreateCoursePayload) => Promise<void>;
      onClose: () => void;
      submitDisabled?: boolean;
    }
  | {
      mode: "edit";
      initialValue: CourseBuilderModel;
      onSubmit: (payload: UpdateCoursePayload) => Promise<void>;
      onClose: () => void;
      submitDisabled?: boolean;
    };

export default function CourseBuilderForm(props: CourseBuilderFormProps) {
  const { mode, onClose, submitDisabled = false } = props;
  const initialValue = props.initialValue;
  const showSaveAsDraft =
    mode === "create" ||
    (mode === "edit" && initialValue && !isBuilderCoursePublished(initialValue));
  const formSchema = useMemo(() => buildFormSchema(mode), [mode]);

  const defaultValues: FormValues = useMemo(() => {
    if (initialValue) {
      return courseBuilderToFormValues(initialValue);
    }
    return {
      grade_name: "",
      terms: [
        {
          id: `t-${uid()}`,
          name: "Term 1",
          lessons: [
            {
              id: `l-${uid()}`,
              name: "Lesson 1",
              units: [emptyUnit()],
            },
          ],
        },
      ],
    };
  }, [initialValue]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (mode === "edit" && initialValue) {
      reset(courseBuilderToFormValues(initialValue));
    }
  }, [mode, initialValue, reset]);

  const termsArray = useFieldArray({ control, name: "terms" });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const terms = watch("terms");

  const addTerm = () => {
    const idx = termsArray.fields.length + 1;
    termsArray.append({
      id: `t-${uid()}`,
      name: `Term ${idx}`,
      lessons: [
        {
          id: `l-${uid()}`,
          name: "Lesson 1",
          units: [emptyUnit()],
        },
      ],
    });
  };

  const submitCreate = (isPublished: boolean) =>
    handleSubmit(async (values) => {
      if (mode !== "create") return;
      await props.onSubmit(toCreateCoursePayload(values, { isPublished }));
    });

  const submitEdit = (isPublished: boolean) =>
    handleSubmit(async (values) => {
      if (mode !== "edit") return;
      await props.onSubmit(
        toUpdateCoursePayload(values, {
          isPublished,
          isActive: props.initialValue.is_active,
        })
      );
    });

  return (
    <div className="bg-white rounded-[12px] w-full overflow-hidden">
      <div className="px-6 md:px-[32px] py-6">
        <div className="text-[20px] font-semibold text-[#1F1F1F]">
          {mode === "edit" ? "Edit Course" : "Create Course"}
        </div>

        <div className="mt-6">
          <div className="text-[14px] font-medium text-[#1F1F1F] mb-2">
            Grade Name<span className="text-[#ef4444]">*</span>
          </div>
          <Controller
            control={control}
            name="grade_name"
            render={({ field }) => (
              <TextField
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter Grade Name"
                error={(errors.grade_name as any)?.message}
                className="max-w-[420px]"
              />
            )}
          />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div className="text-[18px] font-semibold text-[#1F1F1F]">
            Create term
          </div>
          <Button
            type="button"
            variant="primary-outline"
            size="md"
            className="h-[40px] px-4 text-[#5C88C5]"
            onClick={addTerm}
          >
            Add Term +
          </Button>
        </div>

        <div className="mt-4 space-y-6">
          {termsArray.fields.map((termField, termIndex) => {
            const termErrors = (errors.terms?.[termIndex] as any) || {};
            const deleteTerm = () => {
              termsArray.remove(termIndex);
              const current = watch("terms");
              current.forEach((_, i) =>
                setValue(`terms.${i}.name`, `Term ${i + 1}`)
              );
            };

            return (
              <TermSection
                key={termField.id}
                control={control}
                setValue={setValue}
                watch={watch}
                termIndex={termIndex}
                termId={termField.id}
                termNameFallback={termField.name as any}
                sensors={sensors}
                termErrors={termErrors}
                onRemoveTerm={deleteTerm}
                mode={mode}
              />
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-4 border-t border-[#EDEDED] px-6 md:px-[32px] py-4">
        <Button
          type="button"
          variant="light"
          size="md"
          className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
          onClick={onClose}
        >
          Close
        </Button>

        {showSaveAsDraft && (
          <Button
            type="button"
            variant="primary-outline"
            size="md"
            className="w-[160px] h-[48px] text-[#5C88C5] border-[#5C88C5]"
            disabled={isSubmitting || submitDisabled}
            onClick={
              mode === "create" ? submitCreate(false) : submitEdit(false)
            }
          >
            Save as Draft
          </Button>
        )}

        <Button
          type="button"
          variant="primary"
          size="md"
          className="w-[140px] h-[48px] bg-[#5C88C5] text-white"
          disabled={isSubmitting || submitDisabled}
          onClick={
            mode === "create" ? submitCreate(true) : submitEdit(true)
          }
        >
          {mode === "edit"
            ? showSaveAsDraft
              ? "Publish"
              : "Update"
            : "Save"}
        </Button>
      </div>
    </div>
  );
}

